import React, {forwardRef, useEffect, useMemo, useState} from "react";
import SlickSlider from 'react-slick';
import {SvgIcon} from "@mui/material";
import {ReactComponent as Prev} from "../../../../assets/images/slider/previous.svg";
import {ReactComponent as Next} from "../../../../assets/images/slider/next.svg";
import useWindowSize from "../../../hooks/use-window/size";
import classnames from "classnames";

const _settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    autoplay: true,
    autoplaySpeed: 4000,
    centerMode: true,
    pauseOnHover: true,
};

const ArrowWrapper = ({display, ariaLabel, onClick, className = '', extraProps}) => {
    if (display) {
        return (
            <div className={className}
                 onClick={onClick}
                 {...extraProps}
            >
                {ariaLabel === 'next'
                    ? <SvgIcon className={classnames('icon normal d-none')} component={Next}/>
                    : <SvgIcon className={classnames('icon normal d-none')} component={Prev}/>
                }
            </div>
        )

    }
    return <div className='d-none'/>
}

const Slider = forwardRef(({
                               initialIndex = 0,
                               children,
                               dots = true,
                               slidesInRow = 1,
                               autoplay = true,
                               fade = true,
                               nextArrow = false,
                               prevArrow = false,
                               infinite = true,
                               nextArrowProps = {},
                               prevArrowProps = {},
                               swipeToSlide = false,
                               centerMode = true,
                               swipe = true,
                               settings: settingsProps,
                               ...props
                           }, ref) => {
    const [activeIndex, setActiveIndex] = useState(initialIndex);

    const settings = {
        ..._settings,
        dots: dots,
        autoplay: autoplay,
        slidesToShow: slidesInRow,
        slidesToScroll: slidesInRow,
        fade: fade,
        infinite: infinite,
        swipe: swipe,
        nextArrow: <ArrowWrapper display={!!nextArrow} extraProps={nextArrowProps}/>,
        prevArrow: <ArrowWrapper display={!!prevArrow} extraProps={prevArrowProps}/>,
        swipeToSlide: swipeToSlide,
        centerMode: centerMode,
        initialSlide: initialIndex,
        beforeChange: (current, next) => {
            next !== activeIndex && setActiveIndex(next)
        },
        customPaging: (index) => {
            if (typeof activeIndex !== 'number') {
                return <div className='dot'/>
            }
            const distanceFromActiveIndex = Math.abs(activeIndex - index);
            const active = activeIndex === index;
            const oneAway = !active && (distanceFromActiveIndex === 1 || (activeIndex === 0 && index === slidesInRow - 1) || (activeIndex === slidesInRow - 1 && index === 0));
            const twoAway = !active && !oneAway && (distanceFromActiveIndex === 2 || (activeIndex === 0 && index === slidesInRow - 2) || (activeIndex === slidesInRow - 2 && index === 0));
            return (
                <div className={`dot ${activeIndex === index ? 'active' : ''} ${oneAway ? 'one-away' : ''} ${twoAway ? 'two-away' : ''}`}/>
            );
        },
        ...settingsProps,
    };

    return (
        <SlickSlider
            ref={ref}
            {...settings}
            {...props}
        >
            {children}
        </SlickSlider>
    );
})


export const ResponsiveSlider = forwardRef(({
                                                initialIndex = 0,
                                                children,
                                                dots = true,
                                                autoplay = true,
                                                fade = true,
                                                nextArrow = false,
                                                prevArrow = false,
                                                infinite = true,
                                                swipeToSlide = false,
                                                centerMode = true,
                                                swipe = true,
                                                responsiveWidth = 100,
                                                nextArrowProps = {},
                                                prevArrowProps = {},
                                                parentId,
                                                ...props
                                            }, ref) => {
    const [parent, setParent] = useState(document.getElementById(parentId));
    const [activeIndex, setActiveIndex] = useState(initialIndex);
    const [slidesInRow, setSlidesInRow] = useState(Math.floor((parent?.clientWidth ?? 0) / responsiveWidth))
    const windowSize = useWindowSize();

    useEffect(() => {
        setParent(document.getElementById(parentId));
    }, [windowSize])

    useEffect(() => {
        const newSlidesInRow = Math.floor((parent?.clientWidth ?? 0) / responsiveWidth);
        if (newSlidesInRow !== slidesInRow) setSlidesInRow(newSlidesInRow);
    }, [parent?.clientWidth])

    const settings = useMemo(() => ({
        ..._settings,
        dots: dots,
        autoplay: autoplay,
        slidesToShow: slidesInRow,
        slidesToScroll: slidesInRow,
        fade: fade,
        infinite: infinite,
        swipe: swipe,
        nextArrow: <ArrowWrapper display={!!nextArrow} extraProps={nextArrowProps}/>,
        prevArrow: <ArrowWrapper display={!!prevArrow} extraProps={prevArrowProps}/>,
        swipeToSlide: swipeToSlide,
        centerMode: slidesInRow === 1 ? true : centerMode,
        initialSlide: initialIndex,
        beforeChange: (current, next) => {
            next !== activeIndex && setActiveIndex(next)
        },
        customPaging: (index) => {
            if (typeof activeIndex !== 'number') {
                return <div className='dot'/>
            }
            const distanceFromActiveIndex = Math.abs(activeIndex - index);
            const active = activeIndex === index;
            const oneAway = !active && (distanceFromActiveIndex === 1 || (activeIndex === 0 && index === slidesInRow - 1) || (activeIndex === slidesInRow - 1 && index === 0));
            const twoAway = !active && !oneAway && (distanceFromActiveIndex === 2 || (activeIndex === 0 && index === slidesInRow - 2) || (activeIndex === slidesInRow - 2 && index === 0));
            return (
                <div className={`dot ${activeIndex === index ? 'active' : ''} ${oneAway ? 'one-away' : ''} ${twoAway ? 'two-away' : ''}`}/>
            );
        },
    }), [dots, autoplay, slidesInRow, fade, infinite, swipe, nextArrow, nextArrowProps, prevArrow, prevArrowProps, swipeToSlide, centerMode, initialIndex, activeIndex]);

    return (
        <SlickSlider
            ref={ref}
            {...settings}
        >
            {children}
        </SlickSlider>
    );
})


export default Slider;
